/**
 * Styled confirm modal — Qawaem ERP light/dark tokens (not browser `window.confirm`).
 */
import React, { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { AlertTriangle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useModuleConfigTheme } from '../hooks/useModuleConfigTheme';
import { isSaudiBrand } from '../../products/saudi/utils/isSaudiBrand';
import {
  getQawaemModalPanelClass,
  getQawaemModalPanelStyle,
} from '../../products/saudi/components/QawaemErpCrudModal';

const overlayMotion = {
  initial: { opacity: 0 },
  animate: { opacity: 1 },
  exit: { opacity: 0 },
};

const panelMotion = {
  initial: { opacity: 0, scale: 0.92, y: 16 },
  animate: { opacity: 1, scale: 1, y: 0 },
  exit: { opacity: 0, scale: 0.96, y: 12 },
  transition: { type: 'spring', stiffness: 520, damping: 28, mass: 0.65 },
};

/**
 * @param {object} p
 * @param {boolean} p.open
 * @param {string} [p.title]
 * @param {string} [p.message]
 * @param {string} [p.confirmLabel]
 * @param {string} [p.cancelLabel]
 * @param {boolean} [p.danger]
 * @param {boolean} [p.loading]
 * @param {() => void} p.onCancel
 * @param {() => void} p.onConfirm
 */
export function AppConfirmDialog({
  open,
  title,
  message = '',
  confirmLabel,
  cancelLabel,
  danger = true,
  loading = false,
  onCancel,
  onConfirm,
}) {
  const { t } = useTranslation();
  const cfgTheme = useModuleConfigTheme();
  const isLight = cfgTheme.light;
  const qawaem = isSaudiBrand();

  const resolvedTitle = title ?? t('common.confirm', 'تأكيد');
  const resolvedConfirmLabel = confirmLabel ?? t('common.confirm', 'تأكيد');
  const resolvedCancelLabel = cancelLabel ?? t('common.cancel', 'إلغاء');

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape' && !loading) onCancel?.();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, loading, onCancel]);

  const overlayClass = isLight
    ? 'fixed inset-0 z-[200] flex items-center justify-center p-4'
    : 'fixed inset-0 z-[200] flex items-center justify-center p-4';

  const backdropClass = isLight
    ? 'absolute inset-0 bg-black/20 backdrop-blur-md'
    : 'absolute inset-0 bg-black/65 backdrop-blur-sm';

  const iconWrapClass = danger
    ? isLight
      ? 'border-red-200 bg-red-50 text-red-600'
      : 'border-red-400/45 bg-red-500/20 text-red-100'
    : isLight
      ? 'border-blue-200 bg-blue-50 text-blue-600'
      : 'border-blue-400/45 bg-blue-500/20 text-blue-50';

  const cancelBtnClass = isLight
    ? 'rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-medium text-slate-700 transition hover:bg-slate-50'
    : 'rounded-xl border border-white/20 bg-white/10 px-4 py-2.5 text-sm font-medium text-white backdrop-blur-sm transition hover:bg-white/15';

  const confirmBtnClass = danger
    ? 'app-confirm-danger-btn rounded-xl border border-red-500/40 bg-gradient-to-r from-red-600 to-red-700 px-4 py-2.5 text-sm font-semibold !text-white transition hover:from-red-500 hover:to-red-600 disabled:opacity-60'
    : isLight
      ? 'rounded-xl border border-[#6e9b6e]/40 bg-gradient-to-r from-[#6e9b6e] to-[#5a855a] px-4 py-2.5 text-sm font-semibold !text-white transition hover:from-[#7aad7a] hover:to-[#689868] disabled:opacity-60'
      : 'rounded-xl border border-blue-400/50 bg-gradient-to-r from-blue-600 to-blue-700 px-4 py-2.5 text-sm font-semibold !text-white transition hover:from-blue-500 hover:to-blue-600 disabled:opacity-60';

  const messageClass = isLight
    ? 'mt-2 text-sm leading-relaxed text-slate-600 whitespace-pre-wrap'
    : 'mt-2 text-sm leading-relaxed text-white/80 whitespace-pre-wrap';

  const footerBorder = isLight ? 'border-slate-200' : 'border-white/10';

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className={overlayClass}
          role="dialog"
          aria-modal="true"
          aria-labelledby="app-confirm-title"
          initial={overlayMotion.initial}
          animate={overlayMotion.animate}
          exit={overlayMotion.exit}
        >
          <motion.button
            type="button"
            aria-label={t('common.close', 'إغلاق')}
            className={backdropClass}
            onClick={loading ? undefined : onCancel}
            disabled={loading}
          />
          <motion.div
            className={`relative z-10 w-full max-w-md overflow-hidden p-6 ${getQawaemModalPanelClass(isLight)}`}
            style={getQawaemModalPanelStyle(isLight, '28rem')}
            data-app-confirm-dialog=""
            onClick={(e) => e.stopPropagation()}
            initial={panelMotion.initial}
            animate={panelMotion.animate}
            exit={panelMotion.exit}
            transition={panelMotion.transition}
          >
            <div className="flex items-start gap-4">
              <div
                className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border-2 ${iconWrapClass}`}
              >
                <AlertTriangle className="h-5 w-5" aria-hidden />
              </div>
              <div className="min-w-0 flex-1">
                <h2
                  id="app-confirm-title"
                  className={`text-lg font-semibold leading-tight ${cfgTheme.title}`}
                >
                  {resolvedTitle}
                </h2>
                {message ? <p className={messageClass}>{message}</p> : null}
                {qawaem ? (
                  <p className="mt-2 text-xs font-semibold tracking-wide text-red-600">Qawaem ERP</p>
                ) : null}
              </div>
            </div>
            <div className={`mt-6 flex flex-wrap justify-end gap-3 border-t pt-5 ${footerBorder}`}>
              <button type="button" onClick={onCancel} disabled={loading} className={cancelBtnClass}>
                {resolvedCancelLabel}
              </button>
              <button
                type="button"
                onClick={onConfirm}
                disabled={loading}
                className={confirmBtnClass}
              >
                {loading ? t('common.loading', 'جاري…') : resolvedConfirmLabel}
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
